import { useEffect, useState } from "react";
import Typography from "@mui/material/Typography";

import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import TextField from "@mui/material/TextField";

import { BaseButton } from "@/src/component/base/BaseButton";
import { Dropdown } from "@/src/component/base/Dropdown";
import TrackingLogic from "@/src/logic/TrackingLogic";
import VariantTestingLogic from "@/src/logic/VariantTestingLogic";
//import { CommonFrontendService } from "@/src/service/common";
import { GrowthBookType } from "@/src/type/growthbook";
import { ObjectHelper } from "@/src/util";

import styles from "./ManageVariants.module.scss";
import { ProviderHooks } from "@/src/hook";

function getSchemaAndData(componentSchemas: any, apiSchemas: any, htmlComponentId: string, elementData: any) {
  if (!htmlComponentId || !componentSchemas || !apiSchemas || !elementData) {
    return {
      schema: {},
      data: {},
    };
  }

  const { sectionType, componentId, componentType, pathToElement } = TrackingLogic.deconstructComponentId(htmlComponentId);

  // get the schemas based on the item canonical
  const sectionSchema = apiSchemas?.["dynamic-page-sections"];
  const typeSchema = componentSchemas?.[sectionType ?? ""];
  const componentSchema = componentSchemas?.[componentType ?? ""];

  // get the properties for the schemas and determine if property comes from section or type
  // TODO: will need to go down the schema / type tree at some point
  const sectionKeys = Object.keys(sectionSchema?.schema?.attributes ?? {});
  const typeKeys = Object.keys(typeSchema?.attributes ?? {});
  const isSection = sectionKeys.includes(pathToElement ?? "");
  const isType = typeKeys.includes(pathToElement ?? "");

  // get the data based on the element path
  let data = isSection ? elementData[pathToElement ?? ""] : isType ? elementData?.type?.[0]?.[pathToElement ?? ""] : "";
  if (Array.isArray(data)) {
    for (const opt of data) {
      if (`${opt.id}` === `${componentId}`) {
        data = opt;
        break;
      }
    }
  }

  return {
    schema: componentSchema?.attributes,
    data,
  };
}

interface ManageVariantsProps {
  growthBook?: GrowthBookType.GrowthBookAndContentVariants;
  htmlComponentId?: string | null;
  componentSchemas: any;
  apiSchemas: any;
  elementData: any;
  addVariantToExperiment: (variants: any) => void;
}

function ManageVariants({ componentSchemas, apiSchemas, elementData, htmlComponentId, growthBook, addVariantToExperiment }: ManageVariantsProps) {
  const [formData, setFormData] = useState<any>({});
  const experiments = VariantTestingLogic.getExperiments(growthBook ?? {});
  const experimentKey = "available-homes-vs-rent";
  const app = ProviderHooks.useApp();

  useEffect(() => {
    setFormData({});
  }, [htmlComponentId]);

  function getVariants(experimentKey: string) {
    return (experiments ?? {})?.[experimentKey ?? ""]?.meta;
  }

  function getWeights(experimentKey: string, index: number) {
    const weight = (experiments ?? {})?.[experimentKey ?? ""]?.weights?.[index];
    if (typeof weight === "number") {
      return Math.round(weight * 100);
    }
    return weight;
  }

  function getVariationValue(experimentKey: string, index: number) {
    return `${(experiments ?? {})?.[experimentKey ?? ""]?.variations?.[index]}`;
  }

  function getCurrentVariant(experimentKey: string, variantName: string) {
    for (const contentOption of growthBook?.toolkit ?? []) {
      if (contentOption.experimentKey === experimentKey) {
        for (const experiment of contentOption.experiments ?? []) {
          if (experiment.itemCanonical === htmlComponentId) {
            for (const variant of experiment.variants ?? []) {
              if (variant.variantName === variantName) {
                return variant;
              }
            }
          }
        }
      }
    }
  }

  function getValueForVariant(experimentKey: string, variantName: string, uniqueKey: string) {
    if (formData[uniqueKey]) {
      return formData[uniqueKey];
    }
    const keyParts = uniqueKey.split("___");
    const key = keyParts[1];
    return getCurrentVariant(experimentKey, variantName)?.variant?.[key] ?? "";
  }

  function formDataToVariant(experimentKey: string, variantName: string) {
    const formItems: any = {};
    for (const [key, value] of Object.entries(formData ?? {})) {
      const keyParts = key.split("___");
      if (keyParts[0] === variantName) {
        formItems[keyParts[1]] = value;
      }
    }

    const currentVariant = getCurrentVariant(experimentKey, variantName)?.variant ?? {};
    const toPreviewVariant = ObjectHelper.deepMerge(currentVariant, formItems);
    return toPreviewVariant;
  }

  function previewVariant(experimentKey: string, variantName: string) {
    const toPreviewVariant = formDataToVariant(experimentKey, variantName);
    if (!Object.keys(toPreviewVariant).length) {
      const clone = JSON.parse(JSON.stringify(app.growthBook.variants ?? {}));
      for (const key of Object.keys(clone)) {
        if (key === htmlComponentId) {
          delete clone[key];
          break;
        }
      }
      app.overrideGrowthbookVariants(clone);
      return;
    }

    const contentVariants = ObjectHelper.deepMerge(app.growthBook.variants ?? {}, {
      [htmlComponentId ?? ""]: {
        id: 0,
        itemCanonical: htmlComponentId ?? "",
        experimentKey,
        itemKey: "",
        itemId: "",
        itemType: "component",
        variantProject: "",
        variantKey: "",
        variantName: "",
        variant: toPreviewVariant,
      },
    });
    app.overrideGrowthbookVariants(contentVariants);
  }

  function updateExperiment() {
    const contentVariant = (app.growthBook.toolkit ?? [])?.find((variant: any) => variant.experimentKey === experimentKey);
    const toUpdate = contentVariant?.experimentKey
      ? JSON.parse(JSON.stringify(contentVariant))
      : {
          experimentKey,
          project: "flow.life",
          experiments: [],
        };

    const experiment = toUpdate.experiments?.find((experiment: any) => experiment.itemCanonical === htmlComponentId);
    for (const opt of getVariants(experimentKey)) {
      const toPreviewVariant = formDataToVariant(experimentKey, opt.name);
      if (experiment) {
        const variant = experiment.variants?.find((variant: any) => variant.variantName === opt.name);
        if (variant) {
          variant.variant = toPreviewVariant;
        } else {
          if (Object.keys(toPreviewVariant).length) {
            experiment.variants.push({
              variantKey: opt.key,
              variantName: opt.name,
              variant: toPreviewVariant,
            });
          }
        }
      } else {
        if (Object.keys(toPreviewVariant).length) {
          const { componentId, pathToElement } = TrackingLogic.deconstructComponentId(htmlComponentId);
          toUpdate.experiments.push({
            itemCanonical: htmlComponentId,
            itemKey: pathToElement,
            itemId: componentId,
            itemType: "component",
            variants: [
              {
                variantKey: opt.key,
                variantName: opt.name,
                variant: toPreviewVariant,
              },
            ],
          });
        }
      }
    }

    console.log(toUpdate);
    addVariantToExperiment(toUpdate);
  }

  if (!componentSchemas || !apiSchemas || !htmlComponentId) {
    return <></>;
  }

  const { schema, data } = getSchemaAndData(componentSchemas, apiSchemas, htmlComponentId, elementData);
  if (!schema) {
    return <></>;
  }

  return (
    <div className={styles.form}>
      {getVariants(experimentKey)?.map((variant: any, index: number) => {
        return (
          <Card
            variant="outlined"
            key={variant.name}
          >
            <CardContent className={styles.cardContent}>
              <div className={styles.variantHeader}>
                <Typography
                  variant="h6"
                  lineHeight="100%"
                  fontFamily="var(--generation1970Light)"
                >
                  Variant: {variant.name}
                </Typography>
                {getWeights(experimentKey, index) && (
                  <Chip
                    color="ocean"
                    size="small"
                    label={`Split ${getWeights(experimentKey, index)}%`}
                  />
                )}
              </div>
              {getVariationValue(experimentKey, index) && (
                <div className={styles.variantHeader}>
                  <Typography
                    variant="body2"
                    lineHeight="100%"
                  >
                    Value: {getVariationValue(experimentKey, index)}
                  </Typography>
                  <BaseButton
                    variant="text"
                    color="ocean"
                    size="small"
                    className={styles.previewButton}
                    onClick={() => {
                      previewVariant(experimentKey, variant.name);
                    }}
                  >
                    Preview
                  </BaseButton>
                </div>
              )}
              <div>
                {Object.entries(schema).map(([key, value]: any) => {
                  const uniqueKey = `${variant.name}___${key}`;
                  const valueForVariant = index > 0 ? getValueForVariant(experimentKey, variant.name, uniqueKey) : (data?.[key] ?? "");
                  if (value?.type === "text" || value?.type === "string" || value?.type === "integer") {
                    return (
                      <div
                        key={uniqueKey}
                        className={styles.formField}
                      >
                        <TextField
                          fullWidth
                          label={key}
                          value={valueForVariant}
                          onChange={(e) => setFormData({ ...formData, [uniqueKey]: e.target.value })}
                          multiline={value?.type !== "integer"}
                          rows={value?.type === "integer" ? 1 : 4}
                          disabled={index === 0}
                          InputLabelProps={index === 0 || valueForVariant ? { shrink: true } : {}}
                          inputProps={{
                            inputMode: value?.type === "integer" ? "tel" : undefined,
                          }}
                        />
                      </div>
                    );
                  } else if (value?.type === "enumeration") {
                    return (
                      <div
                        key={uniqueKey}
                        className={styles.formField}
                      >
                        <Dropdown
                          id={uniqueKey}
                          options={value?.enum?.map((opt: any) => ({ value: opt, label: opt }))}
                          name={key}
                          label={key}
                          aria-label={key}
                          placeholder={key ?? ""}
                          value={valueForVariant}
                          onChange={(e) => setFormData({ ...formData, [uniqueKey]: e.target.value })}
                          forceShrink={true}
                          disabled={index === 0}
                        />
                      </div>
                    );
                  }
                  return null;
                })}
              </div>
            </CardContent>
          </Card>
        );
      })}
      <BaseButton
        type="submit"
        variant="outlined"
        color="primary"
        size="large"
        fullWidth
        onClick={updateExperiment}
      >
        Add Variants To Experiment
      </BaseButton>
    </div>
  );
}

export default ManageVariants;
